import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { servicesAPI, referralAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Truck, Wrench, Circle, Zap, Fuel, MapPin, LogOut, History, Car, Gift, Copy, Check } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const CustomerDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [referralCode, setReferralCode] = useState('');
  const [creditBalance, setCreditBalance] = useState(0);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const data = await servicesAPI.getServices();
        setServices(data.services);
      } catch (error) {
        console.error('Error fetching services:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, []);

  useEffect(() => {
    const fetchReferralData = async () => {
      try {
        // Get referral code
        const codeData = await referralAPI.getMyCode();
        setReferralCode(codeData.referral_code);
        
        // Get credit balance
        const balanceData = await referralAPI.getBalance();
        setCreditBalance(balanceData.credit_balance);
      } catch (error) {
        console.error('Error fetching referral data:', error);
      }
    };

    fetchReferralData();
  }, []);

  const iconMap = {
    Truck: Truck,
    Wrench: Wrench,
    Circle: Circle,
    Zap: Zap,
    Fuel: Fuel
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleServiceSelect = (service) => {
    navigate('/customer/request', { state: { service } });
  };

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(referralCode);
      setCopied(true);
      toast({
        title: 'Copied!',
        description: 'Referral code copied to clipboard',
      });
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(to bottom, #0F1D2B, #17283A)' }}>
      {/* Header */}
      <div className="border-b" style={{ background: 'linear-gradient(to right, #17283A, #0F1D2B)', borderColor: 'rgba(225, 116, 49, 0.3)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
              <img 
                src="/logo.png" 
                alt="Wheely Rescue Logo" 
                className="w-10 h-10 object-contain"
              />
              <div>
                <h1 className="text-xl font-bold text-white">Wheely</h1>
                <p className="text-xs text-wheely-orange-light hidden sm:block">Welcome back, {user?.name}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => navigate('/customer/garage')}
                style={{ borderColor: 'rgba(225, 116, 49, 0.3)', color: '#E17431' }}
                className="hover:bg-wheely-orange/10"
              >
                <Car className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">My Garage</span>
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/customer/history')}
                style={{ borderColor: 'rgba(225, 116, 49, 0.3)', color: '#E17431' }}
                className="hover:bg-wheely-orange/10"
              >
                <History className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">History</span>
              </Button>
              <Button
                variant="outline"
                onClick={handleLogout}
                style={{ borderColor: 'rgba(235, 152, 104, 0.5)', color: '#EB9868' }}
                className="hover:bg-wheely-orange/10"
              >
                <LogOut className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Logout</span>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-white mb-4">Need Roadside Assistance?</h2>
          <p className="text-xl text-gray-400">Select the service you need</p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {loading ? (
            <div className="col-span-full text-center py-12">
              <div className="w-16 h-16 border-4 border-wheely-orange border-t-transparent rounded-full animate-spin mx-auto"></div>
              <p className="text-gray-400 mt-4">Loading services...</p>
            </div>
          ) : (
            services.map((service) => {
            const IconComponent = iconMap[service.icon];
            return (
              <Card
                key={service.id}
                className="bg-gradient-to-br from-wheely-navy-950 to-wheely-navy-900 border-wheely-orange/20 hover:border-wheely-orange/50 transition-all duration-300 cursor-pointer group hover:scale-105"
                onClick={() => handleServiceSelect(service)}
              >
                <CardHeader>
                  <div className="flex items-center justify-between mb-4">
                    <div className="bg-gradient-to-br from-wheely-orange to-wheely-orange-dark p-3 rounded-xl group-hover:scale-110 transition-transform duration-300">
                      <IconComponent className="w-8 h-8 text-white" />
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-400">Starting at</p>
                      <p className="text-2xl font-bold text-wheely-orange">£{parseFloat(service.base_price || service.basePrice || 0)}</p>
                    </div>
                  </div>
                  <CardTitle className="text-white text-xl">{service.name}</CardTitle>
                  <CardDescription className="text-gray-400">{service.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <Button className="w-full bg-gradient-to-r from-wheely-orange to-wheely-orange-dark hover:from-wheely-orange-dark hover:to-orange-700 text-white font-semibold">
                    Request Service
                  </Button>
                </CardContent>
              </Card>
            );
          })
          )}
        </div>

        {/* Referral Section */}
        {referralCode && (
          <Card className="bg-gradient-to-br from-orange-950 to-wheely-navy-950 border-wheely-orange/30 mb-8">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="bg-gradient-to-br from-wheely-orange to-wheely-orange-dark p-3 rounded-xl">
                    <Gift className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <CardTitle className="text-white text-xl">Refer a Friend, Get £5 Credit!</CardTitle>
                    <CardDescription className="text-gray-300">
                      Share your code. You both get £5 when they spend £50+
                    </CardDescription>
                  </div>
                </div>
                {creditBalance > 0 && (
                  <div className="text-right bg-wheely-orange/20/10 px-4 py-2 rounded-lg border border-wheely-orange/30/30">
                    <p className="text-sm text-wheely-orange-light">Your Credit</p>
                    <p className="text-2xl font-bold text-wheely-orange-light">£{creditBalance.toFixed(2)}</p>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-3">
                <div className="flex-1 bg-wheely-navy-900 border border-wheely-orange/30 rounded-lg px-4 py-3">
                  <p className="text-sm text-gray-400 mb-1">Your Referral Code</p>
                  <p className="text-2xl font-bold text-white tracking-wider">{referralCode}</p>
                </div>
                <Button
                  onClick={handleCopyCode}
                  className="bg-gradient-to-r from-wheely-orange to-wheely-orange-dark hover:from-wheely-orange-dark hover:to-orange-700 text-white"
                >
                  {copied ? (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4 mr-2" />
                      Copy Code
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Quick Info */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="bg-gradient-to-br from-wheely-navy-950 to-wheely-navy-900 border-wheely-orange/20">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="bg-wheely-orange/10 p-2 rounded-lg">
                  <MapPin className="w-5 h-5 text-wheely-orange" />
                </div>
                <CardTitle className="text-white text-lg">Fast Response</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-gray-400">Average arrival time: 15-30 minutes</p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-wheely-navy-950 to-wheely-navy-900 border-wheely-orange/20">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="bg-wheely-orange/10 p-2 rounded-lg">
                  <Truck className="w-5 h-5 text-wheely-orange" />
                </div>
                <CardTitle className="text-white text-lg">Real-Time Tracking</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-gray-400">Track your driver in real-time</p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-wheely-navy-950 to-wheely-navy-900 border-wheely-orange/20">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="bg-wheely-orange/10 p-2 rounded-lg">
                  <Zap className="w-5 h-5 text-wheely-orange" />
                </div>
                <CardTitle className="text-white text-lg">24/7 Available</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-gray-400">We're here whenever you need us</p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default CustomerDashboard;